// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface AffiliateProspect {
  id?: string;
  sales_rep_id?: string;
  name: string;
  email: string;
  company_name?: string;
  website_url?: string;
  social_media_handles?: any;
  audience_size?: number;
  niche_focus?: string;
  lead_source: string;
  pipeline_stage: string;
  notes?: string;
  contact_date?: string;
  next_follow_up?: string;
  tags?: string[];
}

interface AffiliateProspectFormProps {
  open: boolean;
  onClose: () => void;
  prospect?: AffiliateProspect | null;
  onSave: (prospect: AffiliateProspect) => void;
}

const AffiliateProspectForm: React.FC<AffiliateProspectFormProps> = ({ 
  open, 
  onClose, 
  prospect, 
  onSave 
}) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  
  const [formData, setFormData] = useState<AffiliateProspect>({
    name: '',
    email: '',
    company_name: '',
    website_url: '',
    social_media_handles: {},
    audience_size: 0,
    niche_focus: '',
    lead_source: 'manual',
    pipeline_stage: 'prospect',
    notes: '',
    contact_date: '',
    next_follow_up: '',
    tags: [],
    ...prospect
  });

  const leadSources = [
    { value: 'manual', label: 'Manual Entry' },
    { value: 'referral', label: 'Referral' },
    { value: 'website', label: 'Website' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'event', label: 'Event' },
    { value: 'cold_outreach', label: 'Cold Outreach' }
  ];

  const pipelineStages = [
    { value: 'prospect', label: 'Prospect' },
    { value: 'application', label: 'Application' },
    { value: 'review', label: 'Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const nicheFocuses = [
    'Sports Recruiting',
    'Youth Sports',
    'College Athletics',
    'High School Sports',
    'Sports Training',
    'Athletic Scholarships',
    'Sports Media',
    'Sports Coaching',
    'Sports Technology',
    'General Sports'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setLoading(true);
    try {
      const prospectData = {
        ...formData,
        sales_rep_id: profile?.id,
        audience_size: Number(formData.audience_size) || null,
        social_media_handles: formData.social_media_handles || {},
        contact_date: formData.contact_date || null,
        next_follow_up: formData.next_follow_up || null
      };

      if (prospect?.id) {
        // Update existing prospect
        const { data, error } = await supabase
          .from('affiliate_prospects')
          .update(prospectData)
          .eq('id', prospect.id)
          .select()
          .single();

        if (error) throw error;
        onSave(data);
        toast.success('Prospect updated successfully');
      } else {
        // Create new prospect
        const { data, error } = await supabase
          .from('affiliate_prospects')
          .insert(prospectData)
          .select()
          .single();

        if (error) throw error;
        onSave(data);
        toast.success('Prospect created successfully');
      }

      onClose();
    } catch (error) {
      console.error('Error saving prospect:', error);
      toast.error('Failed to save prospect');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {prospect?.id ? 'Edit Affiliate Prospect' : 'Add New Affiliate Prospect'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company">Company/Organization</Label>
                <Input
                  id="company"
                  value={formData.company_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Sports Media Company"
                />
              </div>
              
              <div>
                <Label htmlFor="website">Website URL</Label>
                <Input
                  id="website"
                  value={formData.website_url || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* Affiliate Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Affiliate Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="audience_size">Audience Size</Label>
                <Input
                  id="audience_size"
                  type="number"
                  min="0"
                  value={formData.audience_size || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, audience_size: Number(e.target.value) }))}
                  placeholder="10000"
                />
              </div>
              
              <div>
                <Label htmlFor="niche_focus">Niche Focus</Label>
                <Select 
                  value={formData.niche_focus || ''} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, niche_focus: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select niche" />
                  </SelectTrigger>
                  <SelectContent>
                    {nicheFocuses.map(niche => (
                      <SelectItem key={niche} value={niche}>
                        {niche}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="source">Lead Source</Label>
                <Select 
                  value={formData.lead_source} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, lead_source: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {leadSources.map(source => (
                      <SelectItem key={source.value} value={source.value}>
                        {source.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="stage">Pipeline Stage</Label>
                <Select 
                  value={formData.pipeline_stage} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, pipeline_stage: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelineStages.map(stage => (
                      <SelectItem key={stage.value} value={stage.value}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contact Management */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Contact Management</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_date">Last Contact Date</Label>
                <Input
                  id="contact_date"
                  type="date"
                  value={formData.contact_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_date: e.target.value }))}
                />
              </div>
              
              <div>
                <Label htmlFor="next_follow_up">Next Follow-up Date</Label>
                <Input
                  id="next_follow_up"
                  type="date"
                  value={formData.next_follow_up || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, next_follow_up: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Tags</h3>
            
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags?.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 hover:bg-transparent"
                    onClick={() => removeTag(tag)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" onClick={addTag} variant="outline">
                Add Tag
              </Button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes about this prospect..."
              rows={4}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (prospect?.id ? 'Update Prospect' : 'Create Prospect')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AffiliateProspectForm;